'use client';

import { useEffect } from 'react';

import { useI18n } from '@/components/i18n-provider';
import ErrorState from '@/components/ui/error-state';
import { captureFrontendError, trackEvent } from '@/lib/analytics';
import { canAutoReloadForStaleChunk, isChunkLoadError, reloadForStaleChunk } from '@/lib/chunk-reload';
import { domDivergenceReports } from '@/lib/dom-divergence';

// Filet de sécurité global : avant lui, un crash de rendu React laissait un écran
// blanc sans message ni bouton (24 occurrences la semaine du 17/08/2026, Chrome
// Android). Le bouton recharge complètement la page : après un crash de la phase
// de commit, re-rendre dans le même DOM (reset de Next) échoue souvent au même
// endroit.
export default function AppError({ error }: { error: Error & { digest?: string } }) {
  const { messages } = useI18n();
  const crashMessages = messages.crash;

  useEffect(() => {
    // Chunk périmé après un déploiement : recharger récupère la nouvelle version,
    // sans montrer l'écran d'erreur. Un seul essai par minute (voir chunk-reload).
    if (isChunkLoadError(error) && canAutoReloadForStaleChunk()) {
      trackEvent('stale_chunk_reloaded', { boundary: 'app' });
      reloadForStaleChunk();
      return;
    }
    // Rapport manuel indispensable : une erreur attrapée par une boundary
    // n'atteint plus window.onerror, donc plus les rapporteurs automatiques
    // Sentry/PostHog. Sans cette ligne, poser le filet aurait masqué les crashs.
    console.error('[app-error-boundary]', error);
    captureFrontendError('react-render-boundary', error);
    // Les relevés du mouchard DOM (lib/dom-divergence.ts) disent quel nœud a été
    // déplacé hors de React quand le crash est un removeChild/insertBefore.
    const divergences = domDivergenceReports();
    trackEvent('app_error_screen_shown', {
      boundary: 'app',
      ...(divergences.length ? { dom_divergences: JSON.stringify(divergences) } : {}),
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <div className="w-full max-w-md">
        <ErrorState
          title={crashMessages.title}
          description={crashMessages.body}
          retryLabel={crashMessages.retry}
          onRetry={() => window.location.reload()}
          secondaryLabel={crashMessages.contact}
          onSecondary={() => {
            window.location.href = `mailto:help@liberio.app?subject=${encodeURIComponent(crashMessages.mailSubject)}`;
          }}
        />
      </div>
    </div>
  );
}
